export const FHA_UFMIP_MONEY_TOLERANCE = 0.01;

export type FhaUfmipCaptureStatus =
  | 'not_read'
  | 'not_applicable'
  | 'ok'
  | 'incomplete'
  | 'invalid'
  | 'conflict'
  | 'read_error';

export type FhaUfmipFlowMode =
  | 'not_applicable'
  | 'pending_lender_remittance'
  | 'lender_remitted'
  | 'investor_remitted'
  | 'conflict';

export interface FhaUfmipSourceSnapshot {
  premiumAmount: number | null;
  financedAmount: number | null;
  remittedDate: string | null;
  remittedBy: string | null;
  remittedAmount: number | null;
  verificationCode: string | null;
  captureStatus: FhaUfmipCaptureStatus;
  sourceSignature: string | null;
  captureDetails?: Record<string, unknown> | null;
  lastAttemptedAt?: string | null;
  capturedAt?: string | null;
}

export interface FhaUfmipBridge extends FhaUfmipSourceSnapshot {
  flowMode: FhaUfmipFlowMode;
  borrowerCashAmount: number;
  warehousePremiumAmount: number;
  clearingPremiumAmount: number;
  outsideRemittanceAmount: number;
  companyVarianceAmount: number;
  investorUfmipAmount: number;
  investorAuditBalanced: boolean;
  finalizationErrors: string[];
  isFinalizable: boolean;
}

type FhaInvestorLineItem = {
  category: string;
  line_type: string;
  description: string;
  amount: number;
};

export interface BuildFhaUfmipBridgeInput {
  loanType?: string | null;
  source: FhaUfmipSourceSnapshot;
  lineItems?: FhaInvestorLineItem[];
  investorAuditBalanced?: boolean;
}

function normalizeText(value: string | null | undefined): string {
  return (value || '').trim();
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function positiveMoney(value: number | null | undefined): number {
  return Number.isFinite(value) && Number(value) > FHA_UFMIP_MONEY_TOLERANCE
    ? roundMoney(Number(value))
    : 0;
}

export function isFhaLoanType(loanType: string | null | undefined): boolean {
  return normalizeText(loanType).toUpperCase() === 'FHA';
}

/**
 * Deliberately narrow. Generic government-fee rows remain on the legacy
 * investor-remitted path; only an explicit FHA UFMIP row can suppress the
 * lender-remitted bridge.
 */
export function isExplicitInvestorFhaUfmip(
  item: FhaInvestorLineItem
): boolean {
  if (item.category !== 'investor_cost' || positiveMoney(item.amount) === 0) {
    return false;
  }

  const lineType = normalizeText(item.line_type).toLowerCase();
  if (
    lineType === 'fha_ufmip'
    || lineType === 'fha_ufmip_fee'
    || lineType.startsWith('fha_ufmip_')
  ) {
    return true;
  }

  return /\bFHA\b.*\bUFMIP\b|\bUFMIP\b.*\bFHA\b|Upfront Mortgage Insurance Premium/i
    .test(normalizeText(item.description));
}

export function getExplicitInvestorFhaUfmipAmount(
  lineItems: FhaInvestorLineItem[] = []
): number {
  return roundMoney(
    lineItems
      .filter(isExplicitInvestorFhaUfmip)
      .reduce((sum, item) => sum + positiveMoney(item.amount), 0)
  );
}

function sourceError(status: FhaUfmipCaptureStatus): string | null {
  switch (status) {
    case 'not_read':
      return 'FHA UFMIP fields 969, 1045, and 3079–3082 have not been read from Encompass.';
    case 'read_error':
      return 'FHA UFMIP fields 969, 1045, and 3079–3082 could not be read from Encompass.';
    case 'incomplete':
      return 'FHA UFMIP remittance fields 3079–3082 are incomplete in Encompass.';
    case 'invalid':
      return 'FHA UFMIP fields 969, 1045, or 3079–3082 contain invalid values.';
    case 'conflict':
      return 'FHA UFMIP fields 969, 1045, or 3079–3082 conflict between Encompass sources.';
    default:
      return null;
  }
}

export function buildFhaUfmipBridge(
  input: BuildFhaUfmipBridgeInput
): FhaUfmipBridge {
  const premiumAmount = positiveMoney(input.source.premiumAmount);
  const financedAmount = input.source.financedAmount === null
    ? null
    : roundMoney(Number(input.source.financedAmount));
  const remittedAmount = positiveMoney(input.source.remittedAmount);
  const investorUfmipAmount = getExplicitInvestorFhaUfmipAmount(input.lineItems);
  const investorAuditBalanced = input.investorAuditBalanced ?? false;
  const isFha = isFhaLoanType(input.loanType);
  const finalizationErrors: string[] = [];

  let flowMode: FhaUfmipFlowMode = 'not_applicable';
  if (isFha) {
    if (investorUfmipAmount > 0 && remittedAmount > 0) {
      flowMode = 'conflict';
      finalizationErrors.push(
        'FHA UFMIP appears on the investor advice and also has a company HUD remittance.'
      );
    } else if (investorUfmipAmount > 0) {
      flowMode = 'investor_remitted';
    } else if (remittedAmount > 0) {
      flowMode = 'lender_remitted';
    } else {
      flowMode = 'pending_lender_remittance';
    }
  }

  const captureError = sourceError(input.source.captureStatus);
  if (isFha && captureError) {
    finalizationErrors.push(captureError);
  }

  if (isFha && input.source.captureStatus === 'not_applicable') {
    finalizationErrors.push('Encompass classified this FHA loan as not applicable for UFMIP.');
  }

  if (isFha && premiumAmount === 0) {
    finalizationErrors.push('FHA UFMIP premium (field 969) is missing or zero.');
  }

  if (
    isFha
    && financedAmount !== null
    && (
      !Number.isFinite(financedAmount)
      || financedAmount < 0
      || financedAmount - premiumAmount > FHA_UFMIP_MONEY_TOLERANCE
    )
  ) {
    finalizationErrors.push('FHA UFMIP financed amount (field 1045) is invalid.');
  }

  if (flowMode === 'pending_lender_remittance') {
    finalizationErrors.push('FHA UFMIP has not yet been remitted to HUD.');
  }

  if (flowMode === 'lender_remitted') {
    if (!input.source.remittedDate) {
      finalizationErrors.push('HUD remittance date (field 3079) is missing.');
    }
    if (!normalizeText(input.source.remittedBy)) {
      finalizationErrors.push('HUD remitter (field 3080) is missing.');
    }
    if (!normalizeText(input.source.verificationCode)) {
      finalizationErrors.push('HUD verification code (field 3082) is missing.');
    }
    if (remittedAmount < premiumAmount - FHA_UFMIP_MONEY_TOLERANCE) {
      finalizationErrors.push(
        'HUD remittance (field 3081) is less than the FHA UFMIP premium (field 969); refund accounting requires separate review.'
      );
    }
    if (!investorAuditBalanced) {
      finalizationErrors.push('Investor wire must balance within $0.01 for lender-remitted FHA UFMIP.');
    }
  }

  const lenderSideFlow =
    flowMode === 'pending_lender_remittance'
    || flowMode === 'lender_remitted';
  const warehousePremiumAmount = lenderSideFlow ? premiumAmount : 0;
  const clearingPremiumAmount = lenderSideFlow ? premiumAmount : 0;
  const outsideRemittanceAmount = flowMode === 'lender_remitted' ? remittedAmount : 0;
  const companyVarianceAmount = flowMode === 'lender_remitted'
    ? roundMoney(Math.max(0, outsideRemittanceAmount - clearingPremiumAmount))
    : 0;
  const borrowerCashAmount = premiumAmount > 0 && financedAmount !== null
    ? roundMoney(Math.max(0, premiumAmount - financedAmount))
    : 0;

  return {
    ...input.source,
    premiumAmount: premiumAmount || input.source.premiumAmount,
    financedAmount,
    remittedAmount: remittedAmount || input.source.remittedAmount,
    flowMode,
    borrowerCashAmount,
    warehousePremiumAmount,
    clearingPremiumAmount,
    outsideRemittanceAmount,
    companyVarianceAmount,
    investorUfmipAmount,
    investorAuditBalanced,
    finalizationErrors: [...new Set(finalizationErrors)],
    isFinalizable: finalizationErrors.length === 0
  };
}

export const EMPTY_FHA_UFMIP_SOURCE: FhaUfmipSourceSnapshot = {
  premiumAmount: null,
  financedAmount: null,
  remittedDate: null,
  remittedBy: null,
  remittedAmount: null,
  verificationCode: null,
  captureStatus: 'not_read',
  sourceSignature: null,
  captureDetails: null,
  lastAttemptedAt: null,
  capturedAt: null
};

function nullableMoney(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function captureStatus(value: unknown): FhaUfmipCaptureStatus {
  const statuses: FhaUfmipCaptureStatus[] = [
    'not_read',
    'not_applicable',
    'ok',
    'incomplete',
    'invalid',
    'conflict',
    'read_error'
  ];
  return statuses.includes(value as FhaUfmipCaptureStatus)
    ? value as FhaUfmipCaptureStatus
    : 'not_read';
}

export function fhaUfmipSourceFromCanonicalLoan(
  row: Record<string, unknown>
): FhaUfmipSourceSnapshot {
  return {
    premiumAmount: nullableMoney(row.mi_funding_fee_total_amount),
    financedAmount: nullableMoney(row.mi_funding_fee_financed_amount),
    remittedDate: typeof row.fha_ufmip_remitted_date === 'string'
      ? row.fha_ufmip_remitted_date
      : null,
    remittedBy: typeof row.fha_ufmip_remitted_by === 'string'
      ? row.fha_ufmip_remitted_by
      : null,
    remittedAmount: nullableMoney(row.fha_ufmip_remitted_amount),
    verificationCode: typeof row.fha_ufmip_verification_code === 'string'
      ? row.fha_ufmip_verification_code
      : null,
    captureStatus: captureStatus(row.fha_ufmip_capture_status),
    sourceSignature: typeof row.fha_ufmip_source_signature === 'string'
      ? row.fha_ufmip_source_signature
      : null,
    captureDetails: row.fha_ufmip_capture_details
      && typeof row.fha_ufmip_capture_details === 'object'
      ? row.fha_ufmip_capture_details as Record<string, unknown>
      : null,
    lastAttemptedAt: typeof row.fha_ufmip_last_attempted_at === 'string'
      ? row.fha_ufmip_last_attempted_at
      : null,
    capturedAt: typeof row.fha_ufmip_captured_at === 'string'
      ? row.fha_ufmip_captured_at
      : null
  };
}

export function fhaUfmipSourceFromRecon(
  row: Record<string, unknown>
): FhaUfmipSourceSnapshot {
  return {
    premiumAmount: nullableMoney(row.fha_ufmip_premium_amount),
    financedAmount: nullableMoney(row.fha_ufmip_financed_amount),
    remittedDate: typeof row.fha_ufmip_remitted_date === 'string'
      ? row.fha_ufmip_remitted_date
      : null,
    remittedBy: typeof row.fha_ufmip_remitted_by === 'string'
      ? row.fha_ufmip_remitted_by
      : null,
    remittedAmount: nullableMoney(row.fha_ufmip_remitted_amount),
    verificationCode: typeof row.fha_ufmip_verification_code === 'string'
      ? row.fha_ufmip_verification_code
      : null,
    captureStatus: captureStatus(row.fha_ufmip_capture_status),
    sourceSignature: typeof row.fha_ufmip_source_signature === 'string'
      ? row.fha_ufmip_source_signature
      : null,
    captureDetails: row.fha_ufmip_capture_details
      && typeof row.fha_ufmip_capture_details === 'object'
      ? row.fha_ufmip_capture_details as Record<string, unknown>
      : null,
    capturedAt: typeof row.fha_ufmip_snapshot_at === 'string'
      ? row.fha_ufmip_snapshot_at
      : null
  };
}

export function buildFrozenFhaUfmipBridge(
  row: Record<string, unknown>,
  lineItems: FhaInvestorLineItem[],
  loanType?: string | null
): FhaUfmipBridge {
  const derived = buildFhaUfmipBridge({
    loanType,
    source: fhaUfmipSourceFromRecon(row),
    lineItems,
    investorAuditBalanced: true
  });
  const validFlowModes: FhaUfmipFlowMode[] = [
    'not_applicable',
    'pending_lender_remittance',
    'lender_remitted',
    'investor_remitted',
    'conflict'
  ];
  const flowMode = validFlowModes.includes(row.fha_ufmip_flow_mode as FhaUfmipFlowMode)
    ? row.fha_ufmip_flow_mode as FhaUfmipFlowMode
    : derived.flowMode;
  const lenderSideFlow =
    flowMode === 'pending_lender_remittance'
    || flowMode === 'lender_remitted';

  return {
    ...derived,
    flowMode,
    warehousePremiumAmount: lenderSideFlow ? derived.premiumAmount || 0 : 0,
    clearingPremiumAmount: lenderSideFlow ? derived.premiumAmount || 0 : 0,
    outsideRemittanceAmount: flowMode === 'lender_remitted'
      ? derived.remittedAmount || 0
      : 0,
    companyVarianceAmount: Math.max(
      0,
      nullableMoney(row.fha_ufmip_company_variance)
        ?? derived.companyVarianceAmount
    )
  };
}
